import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'

import { cn } from 'lib/utils'

import { STAGE_HEIGHT, STAGE_WIDTH } from '../layouts'

/**
 * Scale a 1280x720 slide to fit a box, keeping 16:9 (MYD-495).
 *
 * The slide inside is always laid out at full stage size and shrunk with a CSS
 * transform, never re-flowed. That is what makes a sorter thumbnail a faithful
 * miniature of what the projector will show.
 *
 * Two modes:
 *  - `width` given: a fixed scale, no measuring. For thumbnails, of which there
 *    can be a hundred; a ResizeObserver each would be wasteful.
 *  - no `width`: measure the parent and fit inside BOTH its width and height,
 *    centred. For the editor canvas and the presenter.
 */
interface SlideStageProps {
	children: ReactNode
	/** Fixed rendered width in CSS pixels. */
	width?: number
	className?: string
}

export function SlideStage({ children, width, className }: SlideStageProps) {
	const boxRef = useRef<HTMLDivElement>(null)
	const [measured, setMeasured] = useState<number | null>(null)

	useLayoutEffect(() => {
		if (width !== undefined) return
		const box = boxRef.current
		// jsdom and very old browsers: fall back to full size rather than 0.
		if (!box || typeof ResizeObserver === 'undefined') return

		const fit = () => {
			const { width: w, height: h } = box.getBoundingClientRect()
			if (w > 0 && h > 0) setMeasured(Math.min(w / STAGE_WIDTH, h / STAGE_HEIGHT))
		}
		fit()
		const observer = new ResizeObserver(fit)
		observer.observe(box)
		return () => observer.disconnect()
	}, [width])

	const scale = width !== undefined ? width / STAGE_WIDTH : (measured ?? 1)
	const scaled = (
		<div
			className="relative shrink-0 overflow-hidden"
			style={{ width: STAGE_WIDTH * scale, height: STAGE_HEIGHT * scale }}
		>
			<div className="absolute left-0 top-0 origin-top-left" style={{ transform: `scale(${scale})` }}>
				{children}
			</div>
		</div>
	)

	if (width !== undefined) return <div className={className}>{scaled}</div>

	return (
		<div ref={boxRef} className={cn('flex h-full w-full items-center justify-center overflow-hidden', className)}>
			{scaled}
		</div>
	)
}

export default SlideStage
